<template>
  <v-container
    fluid
    class="tk-content">
    <div class="tk-toolbar">
      <div class="d-flex align-center justify-space-between flex-wrap tk-toolbar-row">
        <div class="d-flex align-baseline flex-wrap tk-title-group">
          <h1 class="tk-title">
            Tiktok Order
          </h1>
          <span class="tk-results">{{ resultsLabel }}</span>
        </div>
        <div class="d-flex">
          <v-btn
            outlined
            class="tk-btn tk-btn-cancel mr-2"
            :loading="loading"
            @click="showDialogSyncOrderCancel = true">
            <v-icon
              left
              small>
              mdi-close
            </v-icon>
            Sync Cancel
          </v-btn>
          <v-btn
            color="info"
            class="tk-btn tk-btn-waiting"
            :loading="loading"
            @click="syncWaitingOrder">
            <v-icon
              left
              small>
              mdi-sync
            </v-icon>
            Sync Waiting
          </v-btn>
        </div>
      </div>

      <v-divider class="tk-toolbar-divider" />

      <div class="d-flex align-center flex-wrap tk-toolbar-row">
        <div class="tk-filter-group">
          <label class="tk-filter-label">Brand</label>
          <v-select
            v-model="brandSelected"
            :items="allBrand"
            :menu-props="{ offsetY: true }"
            outlined
            dense
            hide-details
            class="tk-select"
            @change="getItems(true)" />
        </div>

        <v-divider
          vertical
          class="tk-v-divider mx-4 mb-1" />

        <div class="d-flex align-center tk-exception-toggle pb-2">
          <v-switch
            v-model="showExceptionOnly"
            hide-details
            dense
            class="tk-switch ma-0 pa-0 mr-2"
            @change="getItems(true)" />
          <span class="tk-exception-label">Exception Only</span>
        </div>

        <v-spacer />

        <v-text-field
          v-model.trim="inputSearch"
          hide-details
          outlined
          prepend-inner-icon="mdi-magnify"
          placeholder="Search order ID or TikTok ID"
          class="tk-search"
          @keydown.enter="getItems(true)" />
      </div>
    </div>

    <div class="tk-list">
      <template v-if="loading && items.length === 0">
        <div class="tk-loading">
          <v-progress-circular
            indeterminate
            color="info"
            size="28" />
          <span>Loading orders…</span>
        </div>
      </template>

      <template v-else-if="items.length === 0">
        <div class="tk-empty">
          <div class="tk-empty-title">
            No orders match your filters
          </div>
          <div class="tk-empty-subtitle">
            Try clearing the search or turning off Exception Only.
          </div>
        </div>
      </template>

      <template v-else>
        <div
          v-for="item in items"
          :key="item.id"
          class="tk-card">
          <div class="tk-card-header">
            <div class="d-flex align-baseline flex-wrap tk-card-header-ids">
              <span class="tk-order-id">{{ item.orderId }}</span>
              <span class="tk-tiktok-id">TikTok ID: {{ item.tiktokOrderId }}</span>
            </div>
            <span
              class="tk-status-pill"
              :style="{ background: statusStyle(item.tiktokStatus).bg, color: statusStyle(item.tiktokStatus).fg }">
              <span
                class="tk-status-dot"
                :style="{ background: statusStyle(item.tiktokStatus).dot }" />
              {{ statusStyle(item.tiktokStatus).label }}
            </span>
          </div>

          <div class="d-flex flex-wrap tk-card-body">
            <div class="tk-col tk-col-meta">
              <div>
                <div class="tk-label">
                  Created
                </div>
                <div class="tk-value">
                  {{ (item.createTime * 1000) | dateTimeUserFormat }}
                </div>
              </div>
              <div>
                <div class="tk-label">
                  Payment
                </div>
                <div class="tk-value">
                  {{ item.paymentMethodName }}
                </div>
              </div>
            </div>

            <div class="tk-col tk-col-items">
              <div class="tk-items-header">
                <span class="tk-item-photo-col" />
                <span class="tk-item-name-col">Item</span>
                <span class="tk-item-qty-col">Qty</span>
              </div>
              <div
                v-for="(product, index) of item.lineItems"
                :key="item.id+index"
                class="tk-item-row">
                <div class="tk-item-photo-col">
                  <div class="tk-swatch">
                    <v-img
                      :src="product.skuImage"
                      width="100%"
                      height="100%"
                      contain />
                  </div>
                </div>
                <div class="tk-item-name-block">
                  <div class="tk-item-name">
                    {{ product.productName }}
                  </div>
                  <div class="tk-item-option">
                    {{ product.skuName }}
                  </div>
                </div>
                <span class="tk-item-qty">1</span>
              </div>
            </div>

            <div class="tk-col tk-col-shipping">
              <div class="tk-ship-row">
                <span class="tk-ship-label">Type</span>
                <span class="tk-ship-value">{{ item.shippingType || '-' }}</span>
              </div>
              <div class="tk-ship-row">
                <span class="tk-ship-label">Shipped</span>
                <span class="tk-ship-value-plain">{{ item.sellerShippedAt | dateTimeUserFormat }}</span>
              </div>
              <div class="tk-ship-row">
                <span class="tk-ship-label">Ref Code</span>
                <span class="tk-ship-value-plain">{{ item.sellerRefCode || '-' }}</span>
              </div>
              <div class="tk-ship-row">
                <span class="tk-ship-label">Delivered</span>
                <span class="tk-ship-value-plain">{{ item.sellerDeliveredAt | dateTimeUserFormat }}</span>
              </div>
              <div class="tk-ship-row">
                <span class="tk-ship-label">Exception</span>
                <span class="tk-ship-value-plain">{{ item.sellerDeliveryExceptionAt | dateTimeUserFormat }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div class="tk-pagination">
      <div class="d-flex align-center tk-rows-per-page">
        <span class="tk-pagination-text">Rows per page:</span>
        <select
          v-model.number="options.itemsPerPage"
          class="tk-rows-select"
          @change="onRowsPerPageChange">
          <option
            v-for="opt in [15, 25, 50, 100]"
            :key="opt"
            :value="opt">
            {{ opt }}
          </option>
        </select>
      </div>
      <span class="tk-pagination-text">{{ pageLabel }}</span>
      <div class="d-flex tk-pagination-buttons">
        <button
          type="button"
          class="tk-page-btn"
          :disabled="isFirstPage"
          @click="prevPage">
          <v-icon
            small
            :color="isFirstPage ? '#C7C7C7' : '#5A5A5A'">
            mdi-chevron-left
          </v-icon>
        </button>
        <button
          type="button"
          class="tk-page-btn"
          :disabled="isLastPage"
          @click="nextPage">
          <v-icon
            small
            :color="isLastPage ? '#C7C7C7' : '#5A5A5A'">
            mdi-chevron-right
          </v-icon>
        </button>
      </div>
    </div>

    <v-dialog
      v-model="showDialogSyncOrderWaiting"
      max-width="500px">
      <v-card>
        <v-card-title class="headline">
          Sync Waiting Order Result
        </v-card-title>
        <v-card-text>
          <p class="pa-0 mb-0">
            <b> Tiktok order count : </b> {{ syncWaitingOrderResult.tiktokOrderCount }}
          </p>
          <p class="pa-0">
            <b> New order count : </b> {{ syncWaitingOrderResult.newOrderCount }}
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="showDialogSyncOrderCancel"
      max-width="500px">
      <v-card>
        <v-card-title class="headline">
          Sync Cancel Order
        </v-card-title>
        <v-card-text>
          <p class="pa-0">
            <b> Brand : </b> {{ brandSelected }}
          </p>
          <div
            class="d-flex"
            style="justify-items: center; align-items: center;">
            <div style="width: 80%">
              <gw-date-picker
                v-model="dateSelected"
                :loading="loading"
                label="Select Date" />
            </div>

            <v-btn
              color="info"
              small
              class="ml-2"
              :loading="loading"
              @click="syncCancelOrder">
              submit
            </v-btn>
          </div>
          <div v-if="syncCancelOrderResult.tiktokOrderCount">
            <p class="pa-0 mb-0">
              <b> Tiktok order count : </b> {{ syncCancelOrderResult.tiktokOrderCount }}
            </p>
            <p class="pa-0">
              <b> New order count : </b> {{ syncCancelOrderResult.cancelCount }}
            </p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import TiktokProvider from '@/resources/TiktokProvider.js'
import ProductAttributeProvider from '@/resources/ProductAttributeProvider'

const TiktokService = new TiktokProvider()
const ProductAttributeService = new ProductAttributeProvider()

const STATUS_STYLES = {
  UNPAID: {
    bg: '#FDECEA', fg: '#B23B2E', dot: '#EF5350', label: 'Unpaid'
  },
  ON_HOLD: {
    bg: '#FFF3E0', fg: '#B26A1F', dot: '#FF9800', label: 'On Hold'
  },
  AWAITING_SHIPMENT: {
    bg: '#FFF3D6', fg: '#9A7A1F', dot: '#E0AC3C', label: 'Awaiting Shipment'
  },
  PARTIALLY_SHIPPING: {
    bg: '#E1F5F9', fg: '#1FB3C6', dot: '#26C6DA', label: 'Partially Shipping'
  },
  AWAITING_COLLECTION: {
    bg: '#F1E3F7', fg: '#7B3F96', dot: '#9C55BB', label: 'Awaiting Collection'
  },
  IN_TRANSIT: {
    bg: '#E3EEFB', fg: '#2C5FA3', dot: '#4E85D6', label: 'In Transit'
  },
  DELIVERED: {
    bg: '#E6F5E9', fg: '#357A44', dot: '#5CB86E', label: 'Delivered'
  },
  COMPLETED: {
    bg: '#E1E6EF', fg: '#2A3A5C', dot: '#4A5F8A', label: 'Completed'
  },
  CANCELLED: {
    bg: '#EEEEEE', fg: '#757575', dot: '#B0B0B0', label: 'Cancelled'
  }
}

const FALLBACK_STATUS_STYLE = {
  bg: '#EEEEEE', fg: '#757575', dot: '#B0B0B0'
}

export default {
  data () {
    return {
      showDialogSyncOrderCancel: false,
      showDialogSyncOrderWaiting: false,
      dateSelected: null,
      syncCancelOrderResult: {
        tiktokOrderCount: null,
        cancelCount: null
      },
      syncWaitingOrderResult: {
        tiktokOrderCount: 0,
        newOrderCount: 0
      },
      items: [],
      inputSearch: '',
      brandSelected: null,
      showExceptionOnly: false,
      allBrand: [],
      total: 0,
      options: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      loading: false
    }
  },
  computed: {
    resultsLabel () {
      return this.total === 1 ? '1 order' : `${this.total.toLocaleString()} orders`
    },
    totalPages () {
      return Math.max(1, Math.ceil(this.total / this.options.itemsPerPage))
    },
    isFirstPage () {
      return this.options.page <= 1
    },
    isLastPage () {
      return this.options.page >= this.totalPages
    },
    pageLabel () {
      if (this.total === 0) return 'No orders'
      const rangeStart = ((this.options.page - 1) * this.options.itemsPerPage) + 1
      const rangeEnd = Math.min(this.options.page * this.options.itemsPerPage, this.total)
      return `${rangeStart}-${rangeEnd} of ${this.total}`
    }
  },
  watch: {
    showDialogSyncOrderCancel (val) {
      if (val === false) {
        this.syncCancelOrderResult = {
          tiktokOrderCount: null,
          cancelCount: null
        }
      }
    }
  },
  async mounted () {
    await this.getBrand()
    await this.getItems(true)
  },
  methods: {
    statusStyle (status) {
      const style = STATUS_STYLES[status] || FALLBACK_STATUS_STYLE
      return {
        ...style,
        label: style.label || status
      }
    },
    async getBrand () {
      try {
        const { data } = await ProductAttributeService.getProductAttribute('brands', {
          limit: 999,
          sortBy: 'createdAt',
          sortOrder: 'asc'
        })

        this.allBrand = data.results.map((brand) => brand.name)
        this.brandSelected = this.allBrand[0]
      } catch (error) {
        console.error('getAttribute: ', error)
      }
    },
    async syncWaitingOrder () {
      try {
        this.loading = true
        const { data } = await TiktokService.syncWaitingOrder({
            brand: this.brandSelected
        })

        this.syncWaitingOrderResult = {
          tiktokOrderCount: data.tiktokOrderCount,
          newOrderCount: data.newOrders.length
        }
        this.showDialogSyncOrderWaiting = true
      } catch (error) {
        console.error('getItems', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async syncCancelOrder () {
      try {
        if (!this.dateSelected || this.dateSelected === null) {
          this.$store.dispatch('Components/setSnackbar', {
            value: true,
            message: 'Please select date',
            type: 'error'
          })

          return
        }
        this.loading = true
        const { data } = await TiktokService.syncCancelOrder({
            brand: this.brandSelected,
            date: this.dateSelected
        })

        this.syncCancelOrderResult = {
          tiktokOrderCount: data.tiktokOrderCount,
          cancelCount: data.cancelOrders.length
        }
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    onRowsPerPageChange () {
      this.getItems(true)
    },
    prevPage () {
      if (this.isFirstPage) return
      this.options.page -= 1
      this.getItems()
    },
    nextPage () {
      if (this.isLastPage) return
      this.options.page += 1
      this.getItems()
    },
    async getItems (isClear = false) {
      try {
        this.loading = true

        if (isClear) {
          this.items = []
          this.options.page = 1
        }

        if (!this.brandSelected) return

        const query = {
          page: this.options.page,
          limit: this.options.itemsPerPage,
          brand: this.brandSelected,
          search: this.inputSearch,
          hasException: this.showExceptionOnly
        }

        if (!this.showExceptionOnly) {
          query.sortBy = this.options.sortBy[0]
          query.sortOrder = this.options.sortDesc[0] ? 'desc' : 'asc'
        }

        const { data } = await TiktokService.getOrders(query)

        this.items = (data?.results || [])
        this.total = data?.total || 0
      } catch (error) {
        console.error('getItems', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.tk-content {
  padding: 16px 22px 26px;
}
.tk-toolbar {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05), 0 1px 8px rgba(0, 0, 0, 0.04);
  padding: 14px 18px;
  margin-bottom: 13px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: sticky;
  top: 70px;
  z-index: 5;
}
.tk-toolbar-row {
  gap: 20px;
}
.tk-title-group {
  gap: 10px;
}
.tk-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #2b2b2b;
  white-space: nowrap;
}
.tk-results {
  font-size: 11px;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.tk-toolbar-divider {
  margin: 0 !important;
  border-color: #efefef !important;
}
.tk-btn {
  height: 31px !important;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  box-shadow: none !important;
}
.tk-btn-cancel {
  border-color: #dddddd !important;
  color: #5a5a5a !important;
  background: #fff !important;
}
.tk-filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.tk-filter-label {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #9e9e9e;
}
.tk-select {
  min-width: 170px;
}
.tk-select ::v-deep .v-input__control,
.tk-select ::v-deep .v-input__slot {
  min-height: 31px !important;
}
.tk-v-divider {
  height: 30px;
  align-self: flex-end !important;
}
.tk-exception-toggle {
  align-self: flex-end;
}
.tk-exception-label {
  font-size: 13px;
  color: #4a4a4a;
  white-space: nowrap;
}
.tk-search {
  max-width: 320px;
  min-width: 180px;
  align-self: flex-end;
}
.tk-search ::v-deep .v-input__control,
.tk-search ::v-deep .v-input__slot {
  min-height: 40px !important;
}
.tk-search ::v-deep .v-input__prepend-inner {
  margin-top: 0 !important;
  align-self: center !important;
}

.tk-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.tk-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05), 0 1px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}
.tk-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 16px;
  border-bottom: 1px solid #f0f0f0;
  gap: 16px;
  flex-wrap: wrap;
}
.tk-card-header-ids {
  gap: 16px;
}
.tk-order-id {
  font-size: 13px;
  font-weight: 700;
  color: #2b2b2b;
}
.tk-tiktok-id {
  font-size: 11px;
  color: #b0b0b0;
}
.tk-status-pill {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  border-radius: 99px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
}
.tk-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}
.tk-col {
  border-bottom: 1px solid #f0f0f0;
}
.tk-col-meta {
  flex: 1 1 170px;
  min-width: 160px;
  padding: 13px 16px;
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.tk-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #b0b0b0;
  margin-bottom: 3px;
}
.tk-value {
  font-size: 12.5px;
  color: #3a3a3a;
}
.tk-col-items {
  flex: 3 1 280px;
  min-width: 240px;
  padding: 4px 16px;
  border-right: 1px solid #f0f0f0;
}
.tk-items-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 7px 0 5px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #b0b0b0;
}
.tk-item-photo-col {
  width: 34px;
  flex-shrink: 0;
}
.tk-item-name-col {
  flex: 1;
  min-width: 0;
}
.tk-item-qty-col {
  width: 32px;
  flex-shrink: 0;
  text-align: right;
}
.tk-item-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 7px 0;
  border-top: 1px solid #f5f5f5;
}
.tk-swatch {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #f0f0f0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tk-item-name-block {
  flex: 1;
  min-width: 0;
}
.tk-item-name {
  font-size: 12.5px;
  color: #3a3a3a;
  line-height: 1.35;
  overflow-wrap: break-word;
}
.tk-item-option {
  font-size: 11px;
  color: #9e9e9e;
  margin-top: 2px;
}
.tk-item-qty {
  width: 32px;
  flex-shrink: 0;
  text-align: right;
  font-size: 12.5px;
  color: #3a3a3a;
  font-weight: 600;
}
.tk-col-shipping {
  flex: 1 1 190px;
  min-width: 180px;
  padding: 13px 16px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  font-size: 11px;
}
.tk-ship-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}
.tk-ship-label {
  color: #b0b0b0;
}
.tk-ship-value {
  color: #3a3a3a;
  font-weight: 600;
}
.tk-ship-value-plain {
  color: #3a3a3a;
}

.tk-empty {
  background: #fff;
  border: 1px dashed #dddddd;
  border-radius: 10px;
  padding: 60px 24px;
  text-align: center;
}
.tk-empty-title {
  font-size: 14.5px;
  font-weight: 700;
  color: #3a3a3a;
  margin-bottom: 6px;
}
.tk-empty-subtitle {
  font-size: 13px;
  color: #9e9e9e;
}
.tk-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 60px 24px;
  color: #9e9e9e;
  font-size: 13px;
}

.tk-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
  padding: 20px 4px 0;
}
.tk-pagination-text {
  font-size: 12.5px;
  color: #9e9e9e;
}
.tk-rows-per-page {
  gap: 8px;
}
.tk-rows-select {
  height: 30px;
  padding: 0 24px 0 10px;
  border: 1px solid #dddddd;
  border-radius: 6px;
  background: #fff;
  font-size: 12.5px;
  color: #3a3a3a;
}
.tk-pagination-buttons {
  gap: 6px;
}
.tk-page-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #dddddd;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #5a5a5a;
}
.tk-page-btn:disabled {
  border-color: #ebebeb;
  background: #f5f5f5;
  cursor: not-allowed;
}
</style>

<style>
.search-box .v-input__control {
  max-width: unset !important;
  min-height: 28px !important;
  max-height: 28px !important;
}
.search-box .v-input__slot {
  max-width: unset !important;
  min-height: 28px !important;
  max-height: 28px !important;
}
</style>
